import React, { Component } from 'react';
import './App.css';
import AlertMessage from './AlertMessage';
import PatientDetails from './patient/PatientDetails';
import InsuranceDetails from './insurance/InsuranceDetails';
import RemoveRequisition from './requisition/RemoveRequisition';
import withWorkflow from './components/withWorkflow';
import {canAuthorizeOrder, isFullyAccessioned, isGovernmentPayorOrder} from './util/gearOrderUtil';
import MedicareOrderAlert from './components/MedicareOrderAlert';
import AccessionedOrderAlert from './components/AccessionedOrderAlert';
import PaymentServiceContainer from './PaymentServiceContainer';
import { authorizeOrder, submitOrderConfirmationAndPayment, updateMedicalNecessityAcknowledgement } from './util/apiUtils';
import { scrollToMessage } from './util/scrollToMessage';
import MedicalNecessityDisclaimer, {MedicareObligationText} from './components/MedicalNecessityDisclaimer';
import MedicareDiscardAlert from './components/MedicareDiscardAlert';
import SectionWell from './confirm/SectionWell';
import { buildPatientInfoFormFromOrder, validatePatientInfoForm } from './patient/PatientInfoForm';
import UnsavedChangesModal from './confirm/UnsavedChangesModal';
import OrderNotes from './confirm/OrderNotes';
import { calculateAge, getDateFromIso8601String } from './components/DatePicker';
import { ReviewStatus } from './confirm/ReviewStatus';
import PractitionerDetails from './confirm/PractitionerDetails';
import RecommendedProducts from './recommended-products/RecommendedProducts';
import ConnectRememberMe from './phsycian-in-the-loop/rememberMeUtil';
import RequisitionDetails from './confirm/requisition-details/RequisitionDetails';
import { getSupportedBillingMethodNames } from './BillingOptions';
import InsuranceInformation from "./insurance/InsuranceInformation";
import PatientInformation from './confirm/PatientInformation';

class OrderConfirmation extends Component {
	constructor(props) {
		super(props);

		this.editRef = React.createRef();

		// International requisitions do not require billing options
		let domesticRequisitionType = false;
		if (this.props.gearOrder && this.props.gearOrder.practitioner && this.props.gearOrder.practitioner.office) {
			const office = this.props.gearOrder.practitioner.office;
			domesticRequisitionType = office.reqLayoutId === 0 || office.reqLayoutId === 2;
		}

		// This component is used in three different scenarios
		let confirmed = false;
		let currentStep;
		// If this is a 'myGDX' user use the practitioner confirm step
		if (this.props.userInfo.type === 'myGDX') {
			currentStep = 'practitioner_confirmation';
			if (this.props.gearOrder.dataConfirmed) {
				let practConfirmStep = this.props.getWorkflowStep('practitioner_confirmation');
				confirmed = practConfirmStep && practConfirmStep.completed;
			}
		} else if (this.props.practitionerSummary) {
			// If the practitionerSummary prop was passed in use the practitioner_summary step
			currentStep = 'practitioner_summary';
			confirmed = false;
		} else {
			 // For 'prc' users use the patient confirm step
			currentStep = 'patient_confirmation';
			if (this.props.gearOrder.dataConfirmed) {
				let patientConfirmStep = this.props.getWorkflowStep('patient_confirmation');
				confirmed = patientConfirmStep && patientConfirmStep.completed;
			}
		}

		const medicalNecessityAcknowledged = this.props.gearOrder.dateMedicalNecessityAcknowledged;

		// Require pract agreement if the agreement has not already been provided and the logged in user is allowed to authorize the order
		const practAgreementRequired = !this.props.gearOrder.practAgreement && canAuthorizeOrder(this.props.userInfo, this.props.gearOrder);

		this.state = {
			displayRemoveRequisition: false,
			saveForLater: false,
			saveDisabled: false,
			domesticRequisitionType: domesticRequisitionType,
			confirmed: confirmed,
			currentStep: currentStep,
			omsRequisitions: [],
			medicalNecessityRequired: this.props.userInfo.type === 'myGDX' && isGovernmentPayorOrder(this.props.gearOrder) && !medicalNecessityAcknowledged,
			medicalNecessityAcknowledged: {
				value: medicalNecessityAcknowledged
			},
			hasMixedPricingReqs: false,
			editingSection: null,
			isEditSaveInProgress: false,
			redirectWithoutPrompt: false,
			practAgreement: {
				value: !!this.props.gearOrder.practAgreement,
				required: practAgreementRequired,
				valid: null
			}
		};

		this.showRequisition = this.showRequisition.bind(this);
		this.showNoRequisitionsError = this.showNoRequisitionsError.bind(this);
		this.removeClickHandler = this.removeClickHandler.bind(this);
		this.removeRequisitionResponseHandler = this.removeRequisitionResponseHandler.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
        this.goBack = this.goBack.bind(this);
	}

	showRedirectPrompt = (e) => {
		if (this.props.gearOrder.dataConfirmed !== true && this.state.redirectWithoutPrompt === false) {
			e.preventDefault();
			e.returnValue = '';
		}
	}
	
	componentDidMount() {
		window.addEventListener('beforeunload', this.showRedirectPrompt);

		if (this.props.gearOrder.requisitions.length > 0) {
			this.props.setLoading(true);
			let requisitionIdsParam = this.props.gearOrder.requisitions.map(requisition => requisition.id).join("&gearReqIds=")
	        fetch(process.env.REACT_APP_GEAR_REST_URL + "/requisitions?gearReqIds=" + requisitionIdsParam, {
				mode: 'cors',
				credentials: 'include',
				method: 'GET',
				timeout: 1000,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				}
			})
			.then(response => {
				if (response.ok) {
					return response.json();
				} else {
					this.props.showError("An unexpected error has occurred. Please call us at 800-522-4762.")
				}
			})
			.then(json => {
				this.setOmsRequisitions(json);
				
				// Check for incomplete test selections
				if (this.props.userInfo.type === 'myGDX') {
					const gearReqsNeedingTestSelections = findGearRequisitionsNeedingTestSelections(this.props.gearOrder, json);
					this.setState({
						gearReqsNeedingTestSelections: gearReqsNeedingTestSelections
					})
				}
				
			})
			.catch(e => {
				this.props.showError("The network is currently unavailable. Please call us at 800-522-4762.");
			})
			.finally(() => {
				this.props.setLoading(false);
			});
		}
  }

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.showRedirectPrompt);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.gearOrder.requisitions.length !== this.props.gearOrder.requisitions.length && this.props.gearOrder.requisitions.length === 0) {
			this.showNoRequisitionsError();
		}

		if (prevProps.practitionerSummary !== this.props.practitionerSummary) {
				let confirmed = false;
				let currentStep;
				// If this is a 'myGDX' user use the practitioner confirm step
				if (this.props.userInfo.type === 'myGDX') {
					currentStep = 'practitioner_confirmation';
					let practConfirmStep = this.props.getWorkflowStep('practitioner_confirmation');
					confirmed = practConfirmStep && practConfirmStep.completed;
				} else if (this.props.practitionerSummary) {
					// If the practitionerSummary prop was passed in use the practitioner_summary step
					currentStep = 'practitioner_summary';
					confirmed = false;
				} else {
					// For 'prc' users use the patient confirm step
					currentStep = 'patient_confirmation';
					let patientConfirmStep = this.props.getWorkflowStep('patient_confirmation');
					confirmed = patientConfirmStep && patientConfirmStep.completed;
				}
				this.setState({
					confirmed: confirmed,
					currentStep: currentStep,
				});	
		}
	}

	setOmsRequisitions = (omsRequisitions) => {
		const hasMixedPricingReqs = omsRequisitions?.some(req => req?.orderableItem?.mixedComponentPricing);
				
		this.setState({
			omsRequisitions: omsRequisitions,
			hasMixedPricingReqs: hasMixedPricingReqs,
			saveDisabled: this.state.saveDisabled || (hasMixedPricingReqs && this.state.medicalNecessityRequired)
		});
	}
	
	showNoRequisitionsError() {

		let errorMessage = (
			<span>
				Information cannot be confirmed until at least one requisition is added to the order. Click{' '}
				<button
					type="button"
					title="Add Requisition"
					className="btn-link btn-unstyled"
					onClick={() => this.addNewRequisition()}
				>
					here
				</button>{' '}
				to add a requisition.
			</span>
		);

		if (this.props.gearOrder?.dataConfirmed && this.props.userInfo.type === 'myGDX') {
			errorMessage = (
				<span>
					All requisitions have been removed from this order. Click{' '}
					<button
						type="button"
						title="Add Requisition"
						className="btn-link btn-unstyled"
						onClick={() => { this.props.setGearOrder(null); this.props.history.push({ pathname: '/' }); }}
					>
						here
					</button>{' '}
					to start a new order.
				</span>
			);
		}

		this.setState({
			showError: true,
			errorMessage: errorMessage
		});
	}
	
	/**
	 * Remove click handler.
	 *
	 * @param the DOM event
	 */
	removeClickHandler(event) {
		const target = event.currentTarget;
		const saveForLater = !!target.dataset.saveforlater;
		this.setState({
			redirectWithoutPrompt: true,
			displayRemoveRequisition: true,
			saveForLater: saveForLater,
			requisitionToRemove: this.props.gearOrder.requisitions.find(req => req.id === Number(target.dataset.gearreqid))
		});
	}
	
	/**
	 * Handles the response from removing a requisition from an order.
	 *
	 * @param response the fetch response
	 */
	removeRequisitionResponseHandler(response) {
		switch (response.status) {
			case 200:
				response.json().then(json => {
					this.props.setGearOrder(json);
					if (json.requisitions.length === 0) {
						this.props.setOmsRequisition(null);
						this.props.setActiveGearRequisitionId(null);
						this.setState({
							saveDisabled: true
						});
					}
				});
				break;
			case 400:
				this.props.showError("Sorry, this requisition cannot be removed");
				break;
			case 403:
				this.props.showError("Sorry, you are not authorized to remove this requisition");
				break;
			case 404:
				this.props.showError("Sorry, this requisition cannot be found");
				break;
			default:
				this.props.showError("Sorry, an unexpected error has occcured. If you continue to receive this message, please contact us.");
		}
	}
	
	showRequisition(gearReqId, step) {
		if (this.state.editingSection && this.editRef?.current?.isDirty && this.editRef.current.isDirty()) {
			this.setState({
				isShowUnsavedChangesConfirmation: true,
				pendingEditKey: `showRequisition_${gearReqId}_${step}`,
				lastSavedSection: null
			});
			return;
		}

		this.props.setOmsRequisition(null);
		this.props.setActiveGearRequisitionId(gearReqId);
		this.props.updateStep(this.props.gearOrder, step, null, gearReqId);
	}

	/**
	 * Redirect to the panel step with no active requisition set.
	 * This is used when there are no requisitions on the order and the user
	 * clicks the link to add a new req.
	 */
	addNewRequisition = () => {
		this.props.setOmsRequisition(null);
		this.props.setActiveGearRequisitionId(null);

		// For patients, set addNewReq so the panels step will show the search fields.
		if (this.props.userInfo.type === 'prc') {
			this.props.setAddNewReq(true);
		}
		this.props.updateStep(this.props.gearOrder, "panel", null, null);
	}

	/**
	 * This method is called when the user is finished adding a recommended product to the order.
	 *
	 * Push the updated Gear Order to the parent and merge the new OMS Requisition with the existing
	 * omsRequisitions list. The result will be a new requisition well is displayed with the new product
	 * details.
	 *  
	 * @param gearOrder The updated Gear Order after a new requisition has been saved
	 * @param omsRequisition The OMS Requisition struction for the newly saved requisition
	 */
	loadSavedRecommendedProduct = ({ gearOrder, omsRequisition }) => {

		const newOrderableItemId = omsRequisition?.orderableItem?.id;
		let updatedOmsRequisitions;
		if (!this.state.omsRequisitions?.length) {
			updatedOmsRequisitions = [omsRequisition];
		} else if (!this.state.omsRequisitions.some(req => req?.orderableItem?.id === newOrderableItemId)) {
			updatedOmsRequisitions = [...this.state.omsRequisitions, omsRequisition];
		} else {
			// No-op. New omsRequisition already exists in the omsRequisitions list
		}

		this.setOmsRequisitions(updatedOmsRequisitions || this.state.omsRequisitions);

		// Keep track of the newly added Orderable Item Id so we can highlight the new requisition well
		this.setState(prevState => {
			return {
				recommendedProductOrderableItemIds: [...(prevState.recommendedProductOrderableItemIds || []), newOrderableItemId]
			}
		}, () => {
			this.props.setGearOrder(gearOrder);
		});
	}

	changeStep = (event, newStep) => {
		event && event.preventDefault();

		if (this.state.editingSection && this.editRef?.current?.isDirty && this.editRef.current.isDirty()) {
			this.setState({
				isShowUnsavedChangesConfirmation: true,
				pendingEditKey: `navigate_${newStep}`,
				lastSavedSection: null
			});
			return;
		}

		this.props.updateStep(this.props.gearOrder, newStep);
	}

	editInsurance = (event) => {
		this.changeStep(event, "insurance");
	}

	editPatient = (event) => {
		this.changeStep(event, "patient");
	}

    goBack(e){
		e.preventDefault();
		this.props.updateStep(this.props.gearOrder, this.state.currentStep, 'Back');
	}

	/**
	 * Expand a section to be edited.
	 * 
	 * First, check if a different section is already open and if the opened section has unsaved changes. If so, show a modal to confirm that changes will be lost.
	 * If there are no unsaved changes to worry about, update state to expand the section to be edited.
	 * 
	 * @param editKey A key in the format '{section}_{reqId}' identifying which section will be edited
	 */
	editSection = (editKey) => {
		if (this.state.editingSection && this.editRef?.current?.isDirty && this.editRef.current.isDirty()) {
			this.setState({
				isShowUnsavedChangesConfirmation: true,
				pendingEditKey: editKey,
				lastSavedSection: null
			});
		} else {
			this.setState({
				editingSection: editKey,
				isShowUnsavedChangesConfirmation: false,
				lastSavedSection: null,
				inlineSaveError: null
			}, () => {
				scrollToMessage('.edit-expanded');
			});
		}
	}

	/**
	 * Collapse the section being edited without saving changes.
	 */
	cancelEdit = () => {
		const prevEditingSection = this.state.editingSection;
		this.setState({editingSection: null}, () => {
			this.scrollToEditableSection(prevEditingSection);
		});
	};

	scrollToEditableSection = (editingSection) => {
		const dataStep = editingSection.replace('_', '-');
		const element = document.querySelector("[data-step=" + CSS.escape(dataStep) + "]");
		if (element) {
			element.scrollIntoView({block: 'center'});
		}
	}

	/**
	 * Confirms that unsaved changes can be undone and expands the new section to be edited if needed.
	 */
	clearUnsavedChanges = (event) => {
		const pendingEditKey = this.state.pendingEditKey;

		this.setState({
			isShowUnsavedChangesConfirmation: false,
			editingSection: pendingEditKey,
			pendingEditKey: null,
			inlineSaveError: null
		}, () => {
			/* If 'submit' is the pending edit that means we were trying to submit the page before the unsaved changes confirmation popped up.
			 * Now that the user has confirmed they want to ignore unsaved changes we can fire the submit action again.
			 */
			if (pendingEditKey === 'submit') {
				// Must create and pass a 'dummy' event because handleSubmit calls event.preventDefault()
				this.handleSubmit({ preventDefault: () => { } });
			} else if (pendingEditKey?.startsWith('navigate_')) {
				// If the pending edit starts with 'navigate_' it means the user was navigating to a different page before confirming to lose unsaved changes.
				// Redirect to the desired page
				const nextStep = pendingEditKey.substr(pendingEditKey.indexOf('_') + 1);
				this.changeStep(null, nextStep);
			} else if (pendingEditKey?.startsWith('showRequisition_')) {
				// The showRequisition method was being called to navigate.
				const [, gearReqId, step] = pendingEditKey.split('_');
				this.showRequisition(gearReqId, step);
			}
		});
	}

	/**
	 * Scrolls to the currently expanded edit section after a user chooses to keep unsaved changes.
	 */
	handleUnsavedChanges = () => {
		this.setState({ isShowUnsavedChangesConfirmation: false, pendingEditKey: null });
		scrollToMessage('.edit-expanded');
	}

	simluateSave = () => {
		return new Promise((resolve, reject) => {
			setTimeout(() => {
				console.log('Timeout finished');
				let updatedOrder = Object.assign({}, this.props.gearOrder);
				if (updatedOrder?.requisitions) {
					updatedOrder.requisitions.forEach(req => req.dateLastSampleCollected = null);
				}
				resolve(this.props.gearOrder);
			}, 1000);
		})
	}

	/**
	 * Saves the values of the section being edited.
	 * 
	 * Each section will handle its own validation and save.
	 * When validation fails we expect a rejected Promise with the string 'ValidationError'.
	 * When a save is successful we expect a resolved Promise with the updated GearOrder object.
	 * Any other rejected Promise is unexpected and will result in an error message being displayed.
	 */
	saveEdit = () => {
		/* isEditSaveInProgress will trigger a shield and spinner icon to overlay the section being edited
		 * and also triggers the removal of all edit buttons on the page.
		 */ 
		this.setState({ isEditSaveInProgress: true, inlineSaveError: null });

		// this.simluateSave()
		this.editRef.current.save()
			.then(gearOrder => {
				const prevEditingSection = this.state.editingSection;
				this.setState(prevState => ({
					editingSection: null,
					lastSavedSection: prevState.editingSection
				}), () => {
					this.scrollToEditableSection(prevEditingSection);
				});
				this.props.setGearOrder(gearOrder);
			})
			.catch(err => {
				if (err === 'ValidationError') {
					// Do nothing. The section should show the validation errors.
				} else {
					this.setState(
						{
							inlineSaveError:
								// err?.message ||
								'An unexpected error has occurred. Please try again or call us at 800-522-4762 if the issue continues.'
						},
						() => scrollToMessage()
					);

				}
			})
			.finally(() => {
				this.setState({ isEditSaveInProgress: false });
			});
	}

	/**
	 * Sets the function that will be used to validate and build payment details
	 */
	setPaymentValidationHandler = (fnc) => {
		this.paymentValidationHandler = fnc;
	}

	/**
	 * Update the medicalNecessityAcknowledged state value and send a request to save the new value.
	 */
	setMedicalNecessityAcknowledged = (evt) => {
		let checked = evt.target.checked;
		let medicalNecessityAcknowledged = {
			value: checked ? new Date() : null
		}
		
		this.setState({
			medicalNecessityAcknowledged: this.validateMedicalNecessityAcknowledged(medicalNecessityAcknowledged)
		});

		// Persist the selection
		updateMedicalNecessityAcknowledgement(this.props.gearOrder.id, medicalNecessityAcknowledged.value)
			.catch(err => {
				// If an eror occurred while trying to save the acknowledgment date show a message
				if (medicalNecessityAcknowledged.value) {
					this.setState({
						medicalNecessityAcknowledged: {
							value: null,
							valid: false,
							message: 'An error occurred while saving the selection. Please try again.'
						}
					});
				}
			});
	}

	/**
	 * Validate the medicalNecessityAcknowledged value is not empty if the acknowlegement is required.
	 */
	validateMedicalNecessityAcknowledged = (medicalNecessityAcknowledged) => {
		medicalNecessityAcknowledged.valid = true;
		medicalNecessityAcknowledged.message = '';

		// Validate medicalNecessityAcknowledged if this is a Medicare order and a practitioner user
		if (this.state.medicalNecessityRequired) {
			if (!medicalNecessityAcknowledged.value) {
				medicalNecessityAcknowledged.valid = false;
				medicalNecessityAcknowledged.message = "Please acknowledge you have read the definition of medical necessity.";
			}
		}

		return medicalNecessityAcknowledged;
	}

	/**
	 * Send a request to save practitioner agreement.
	 */
	setPractAgreement = (event) => {
		let checked = event.target.checked;

		// Unchecking shouldn't be possible. Return without doing anything if it does happen.
		if (!checked) {
			return;
		}

		this.setState((prevState) => {
			return {
				practAgreement: {
					...prevState.practAgreement,
					saveInProgress: true
				}
			};
		});

		// Persist the authorization
		authorizeOrder(this.props.gearOrder.id)
			.then((response) => {
				this.setState({
					practAgreement: {
						value: true,
						required: true,
						valid: true,
						saveInProgress: false
					}
				});

				/*
				 * Update the order in case we jump to the next step without saving the confirmation step.
				 * In that case, we wouldn't be fetching fresh order details and the summary page would show
				 * messaging as if the order was unauthorized.
				 */
				let updatedOrder = { ...this.props.gearOrder };
				updatedOrder.practAgreement = true;
				this.props.setGearOrder(updatedOrder);
			})
			.catch((err) => {
				// If an eror occurred while trying to save the agreement show a message
				let message = 'An error occurred while saving the authorization. Please try again.';
				if (err?.response?.status === 403) {
					message = 'You do not have permission to authorize this order.';
				}

				this.setState({
					practAgreement: {
						value: false,
						required: true,
						valid: false,
						saveInProgress: false,
						message: message
					}
				});
			});
	};

	handleSubmit(event) {
		event.preventDefault();

		if (this.state.currentStep === 'practitioner_summary') {
			this.props.updateStep(this.props.gearOrder, this.state.currentStep, 'Next');
			return;
		}

		if (this.state.editingSection && this.editRef?.current?.isDirty && this.editRef.current.isDirty()) {
			this.setState({
				isShowUnsavedChangesConfirmation: true,
				pendingEditKey: 'submit',
				lastSavedSection: null
			});
			return;
		}

		// If the payment validation handler has been set, call it to validate and build the payment object
		if (this.paymentValidationHandler) {
			this.paymentValidationHandler(event);
		} else {
			// No paymentValidationHandler was set. Confirm the order without payment details
			this.validateMedicalNecessityAndSubmit();
		}
	}

	/**
	 * Validate that the Medical Necessity checkbox is checked, if required, and submit the confirmation/payment.
	 * 
	 * @param orderPayment - The payment details provided if the submit is triggered from the payment form
	 */
	validateMedicalNecessityAndSubmit = (orderPayment) => {
		this.setState({
			medicalNecessityAcknowledged: this.validateMedicalNecessityAcknowledged(this.state.medicalNecessityAcknowledged)
		}, () => {
			const medicalNecessityValid = this.state.medicalNecessityAcknowledged.valid;
			if (medicalNecessityValid) {
				this.confirmAndPay(orderPayment);
			} else {
				scrollToMessage();
			}
		});
	}

	confirmAndPay = (orderPayment) => {
		// Verify practAgreement if required
		if (this.state.practAgreement.required && !this.state.practAgreement.valid) {
			// Don't do anything if a save is in progress
			if (this.state.practAgreement.saveInProgress) {
				return;
			}

			this.setState(prevState => {
				return {
					practAgreement: {
						...prevState.practAgreement,
						valid: false,
						message: 'Please verify order authorization.'
					}
				}
			}, scrollToMessage);

			return;
		}

		// If the order has already been confirmed and there is no new payment information to save skip to the next step
		if (this.state.confirmed && !this.hasPaymentData(orderPayment)) {
			this.props.updateStep(this.props.gearOrder, this.state.currentStep, 'Next');
			return;
		}

		this.setState({
			saveDisabled: true,
			showSubmitError: false,
			submitErrorMessage: null,
			resetPayPalPayment: false
		});
		this.props.setLoading(true);

		submitOrderConfirmationAndPayment(this.props.gearOrder.id, orderPayment)
			.then((json) => {
				if (json.gearOrder) {
					this.props.setGearOrder(json.gearOrder);
				}

				if (json.error) {
					return Promise.reject(json.error);
				} else {
					ConnectRememberMe.clear();
					this.props.updateStep(json.gearOrder, this.state.currentStep, 'Next');
				}
			})
			.catch((e) => {
				let errorMessage = '';
				if (e?.error === 'RequisitionPaymentException' || e?.error === 'ExpeditedShippingPaymentException' || e?.error === 'PitlDiscountNotFoundException' || e?.error === 'DiscountNotFoundException') {
					errorMessage = e.message || '';
				}

				let errorEl;
				if (errorMessage) {
					if (e?.error === 'ExpeditedShippingPaymentException' && this.props.userInfo.type === 'myGDX') {
						errorMessage = 'There was a problem with your shipping charges payment. ' + errorMessage;
					} else if (e?.error === 'RequisitionPaymentException' && this.props.userInfo.type === 'myGDX') {
						errorMessage = 'There was a problem with your test payment. ' + errorMessage;
					}
					errorEl = <span>{errorMessage}</span>;
				} else if (e?.error === 'ExpeditedShippingPaymentException') {
					errorEl = (
						<span>
							An unexpected error occurred when submitting your shipping charges payment. Please try again
							or call us at 800-522-4762 or contact <a href="/mygdx/contact">Customer Support</a> for
							help.
						</span>
					);
				} else if (e?.error === 'RequisitionPaymentException') {
					errorEl = (
						<span>
							An unexpected error occurred when submitting your test payment. Please review your details and
							try again or call us at 800-522-4762 or contact <a href="/mygdx/contact">Customer Support</a> for help.
						</span>
					);
				} else {
					errorEl = (
						<span>
							An unexpected error occurred when submitting your order. Please call us at 800-522-4762 or contact{' '}
							<a href="/mygdx/contact">Customer Support</a>.
						</span>
					);
				}

				this.setState({
					showSubmitError: true,
					submitErrorMessage: errorEl,
					saveDisabled: false
				});

				/* 
				 * Payment controls were hidden after successful PayPal approval but the order submission failed.
				 * Setting resetPayPalPayment will re-enable the payment controls if payment needs to be attempted
				 * again.
				 */
				let resetPayPalPayment = false;
				if (orderPayment?.payments?.some(payment => payment.paymentType === 'paypal')) {
					resetPayPalPayment = true;
				}
				this.setState({ resetPayPalPayment: resetPayPalPayment });

				// Scroll up to the error message
				scrollToMessage();
			})
			.finally(() => {
				this.props.setLoading(false);
			});
	}

	hasPaymentData = (orderPayment) => {
		if (!orderPayment) {
			return false;
		}

		// Only consider patientAgreement if the patient is submitting the order
		if (orderPayment.patientAgreement && this.props.userInfo.type !== 'myGDX') {
			return true;
		}

		if (Array.isArray(orderPayment.payments) && orderPayment.payments.length) {
			return true;
		}

		return false;
	}

	/**
	 * Creates the JSX component for payment.
	 * Before the order has been confirmed, this component displays the shipping method, price, and credit card form.
	 * After confirmation, this component displays the payment amount and transaction id.
	 *
	 * @return JSX requisition component
	 */
	buildPaymentDisplay = () => {

		// Don't show payment inputs for 'practitionerSummary'
		if (this.props.practitionerSummary) {
			return null;
		}

		// Don't show payment if this is the first PITL recollection
		if (
			this.props.gearOrder.pitlRecollectionOrderSummary &&
			!this.props.gearOrder.pitlRecollectionOrderSummary.paymentRequired
		) {
			return null;
		}

		// Do not show payment step for covidRegistration with insurance
		if (
			this.props.gearOrder?.requisitions?.every(
				(req) => req.covidRegistration && req.billingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE
			)
		) {
			return null;
		}
	
		return (
			<div data-step="payment">
				<SectionWell sectionId='paymentInfo' title='Payment Information'>
					<>
						<div className="separator-2"></div>
						<PaymentServiceContainer
							setPaymentHandler={this.setPaymentValidationHandler}
							userInfo={this.props.userInfo}
							gearOrder={this.props.gearOrder}
							setDirty={this.props.setDirty}
							showError={this.props.showError}
							setLoading={this.props.setLoading}
							setGearOrder={this.props.setGearOrder}
							prices={this.props.prices}
							doNotQuotePrices={this.props.doNotQuotePrices}
							onSubmit={this.validateMedicalNecessityAndSubmit}
							applyDiscount={this.props.applyDiscount}
							resetPayPalPayment={this.state.resetPayPalPayment}
							reloadPrices={this.props.reloadPrices}
							slowFetch={this.props.slowFetch}
						/>
					</>
				</SectionWell>
			</div>
		);
	}

	/**
	 * Determines if the page can be saved based on details of other steps.
	 */
	canUserSaveStep = () => {
		/*
		 * Always honor state.saveDisabled if it is 'true'.
		 * It can be set 'true' for a variety of reasons (Like deleting the last requisition on the order).
		 */
		if (this.state.saveDisabled) {
			return false;
		}

		// Do not allow save if an inline edit save is in progress
		if (this.state.isEditSaveInProgress) {
			return false;
		}

		// Do not allow save if the practitioner agreement save is in progress
		if (this.state.practAgreement.saveInProgress) {
			return false;
		}

		// No validation is required on the practitioner summary screen (First screen of patient-first registration)
		if (this.state.currentStep === 'practitioner_summary') {
			return true;
		}

		let canUserSave = true;
		let insuranceStep = this.props.getWorkflowStep('insurance');
		let insuranceStepRequired = insuranceStep && !insuranceStep.notApplicable;
		const isPrcUser = this.props.userInfo.type === 'prc';

		// For prc users, don't allow save if the patient step is not complete or if the insurance step is required but not complete
		if (
			isPrcUser &&
			(!this.props.gearOrder.patientInfo ||
				this.props.getWorkflowStep('patient')?.skipped ||
				(insuranceStepRequired &&
					(!this.props.gearOrder.insurance || this.props.getWorkflowStep('insurance')?.skipped)))
		) {
			canUserSave = false;
		}

		if (this.props.gearOrder.requisitions.length === 0) {
			canUserSave = false;
		} else if (
			this.state.domesticRequisitionType &&
			this.props.gearOrder.requisitions.find(
				(req) =>
					(!req.billingMethod && (!req.dropShip || isPrcUser)) || // myGDX Drop-ship doesn't require a billing method
					(!isPrcUser &&
						req.icdCodes.length === 0 &&
						req.billingMethod &&
						(req.billingMethod.indexOf('Bill Insurance') > -1 ||
							req.billingMethod.indexOf('Insurance - TESTING') > -1 ||
							req.billingMethod.indexOf('Medicare') > -1))
			) !== undefined
		) {
			canUserSave = false;
		}

		// For 'prc' users and drop-ship orders, validate that all required patient info has been entered
		if (canUserSave && (isPrcUser || this.props.gearOrder.containsDropShipRequisition)) {
			const validatedPatientInfoForm = this.getValidatedPatientInfoForm(this.props.gearOrder);
			canUserSave =
				(validatedPatientInfoForm && Object.values(validatedPatientInfoForm).every((prop) => prop.valid)) ??
				false;
		}

		// Require patientInfo for drop-ship orders
		if (!this.props.gearOrder.patientInfo && this.props.gearOrder.containsDropShipRequisition) {
			canUserSave = false;
		}

		// Disable save for clients if any reqs require test selections
		if (!isPrcUser && this.state.gearReqsNeedingTestSelections?.length) {
			canUserSave = false;
		}

		// Disable save for clients if any billing step was skipped by the system (client has not taken action on billing option page)
		if (!isPrcUser && this.props.gearOrder.workflow?.steps?.find((step) => step.id === 3 && step.skipped && step.userType === 'system')) {
			canUserSave = false;
		}

		return canUserSave;
	}

	/**
	 * Get the validated patient info form.
	 */
	getValidatedPatientInfoForm = (gearOrder) => {
		let validatedForm = validatePatientInfoForm(buildPatientInfoFormFromOrder(gearOrder), gearOrder, true);

		// HD-71279: Do not validate confirmedAge on the confirmation page
		if (validatedForm?.dateOfBirth?.confirmedAge === false) {
			validatedForm.dateOfBirth.valid = true;
			validatedForm.dateOfBirth.confirmedAge = null;
			validatedForm.dateOfBirth.message = "";
		}

		return validatedForm;
	}

	/**
	 * Checks if all required prices for a Connect order are loaded and without error.
	 */
	isPITLPriceLoaded = () => {
		if (!this.props.gearOrder.physicianInTheLoop || this.state.confirmed) {
			return true;
		}

		const reqsRequiringPrices = this.props.gearOrder.requisitions.filter(
			(req) => !req.accessionNo && !req.paymentInfo
		);

		return reqsRequiringPrices.every((req) => { 
			const reqPrice = this.props.prices.find((price) => price.gearRequisitionId === req.id);
			return !!reqPrice && !reqPrice.placeholder && !reqPrice.error && !reqPrice.configError;
		});
	};

	render () {
		let customerType = this.props.userInfo.type;
		
		// SCR-6308: Can edit until all requisitions on the order have been accessioned
		const fullyAccessioned = isFullyAccessioned(this.props.gearOrder);
		let canEditAny = !this.state.isEditSaveInProgress && !this.props.practitionerSummary && (this.props.userInfo.adminUser === 'true' || !fullyAccessioned);
		
		const isSaveDisabled =
			(!fullyAccessioned && !this.canUserSaveStep()) ||
			!this.isPITLPriceLoaded();

		// expecting the format '{section}_{gearReqId}'
		const [editingSection, editingReq] = this.state.editingSection?.split('_') || [];
		const [lastSavedSection, lastSavedReq] = this.state.lastSavedSection?.split('_') || [];

		// Get PatientInfo validation form and use it to determine if step is incomplete. Do not do this when on 'practitioner_summary' step.
		let patientSection, patientDetails;
		const patientInfo = this.props.gearOrder.patientInfo;
		let canEditPatient = canEditAny;
		let practitionerSummary = this.state.currentStep === 'practitioner_summary';
		if (practitionerSummary) {
			patientDetails = null;
		} else {
			const validatedPatientInfoForm = this.getValidatedPatientInfoForm(this.props.gearOrder); 
			if (!fullyAccessioned && (!validatedPatientInfoForm || Object.values(validatedPatientInfoForm).some((prop) => !prop.valid))) {
				if (customerType === 'prc' || !patientInfo || this.props.gearOrder.containsDropShipRequisition) {
					canEditPatient = false;
					patientDetails = 
						<AlertMessage type="danger" additionalCssClass="alert-icon-warning mt-10">
							<span>
								Click{' '}
								<button
									type="button"
									className="btn-link btn-unstyled link-underline"
									onClick={() => this.editSection(`patient`)}
								>
									<strong>here</strong>
								</button>{' '}
								to provide missing Patient information.
							</span>
						</AlertMessage>
				} else {
					patientDetails = <PatientDetails patient={patientInfo} userInfo={this.props.userInfo} missingPatientInfo={true} />
				}
			} else {
				patientDetails = <PatientDetails patient={patientInfo} userInfo={this.props.userInfo} />
			}
		}
		if (patientDetails) {
			patientSection = (
				<PatientInformation
					gearOrder={this.props.gearOrder}
					userInfo={this.props.userInfo}
					patientDetails={patientDetails}
					insuranceOptions={this.props.insuranceOptions}
					setInsuranceOptions={this.props.setInsuranceOptions}
					editRef={this.editRef}
					triggerInlineSave={this.saveEdit}
					editingSection={editingSection}
					lastSavedSection={lastSavedSection}
					onEdit={canEditPatient && this.editSection}
					cancelEdit={this.cancelEdit}
					saveEdit={this.saveEdit}
					isEditSaveInProgress={this.state.isEditSaveInProgress}
					inlineSaveError={this.state.inlineSaveError}
				/>
			);
		}

		let insuranceInfo = this.props.gearOrder.insurance;
		let secondaryInsuranceInfo = this.props.gearOrder.secondaryInsurance;
		let insuranceStep = this.props.getWorkflowStep('insurance');
		let insuranceSection, insuranceDetails, secondaryInsuranceDetails;
		let canEditInsurance = canEditAny;
		// No insurance required
		if( !insuranceStep || insuranceStep.notApplicable ) {
			 insuranceDetails = null;
			 secondaryInsuranceDetails = null;
		} else if (!insuranceInfo || insuranceStep.skipped) {
			if (fullyAccessioned || practitionerSummary) {
				insuranceDetails = null;
				secondaryInsuranceDetails = null;
			} else if( customerType === 'prc') {
				canEditInsurance = false;
				insuranceDetails = (
					<AlertMessage type="danger" additionalCssClass="alert-icon-warning mt-10">
						<span>
							Click{' '}
							<button
								type="button"
								className="btn-link btn-unstyled link-underline"
								onClick={() => this.editSection(`insurance_${this.props.gearOrder.id}`)}
							>
								<strong>here</strong>
							</button>{' '}
							to provide missing Insurance information.
						</span>
					</AlertMessage>
				);
				secondaryInsuranceDetails = null;
			 } else {
				insuranceDetails = (
					<AlertMessage type="info">
						<strong>Incomplete. The patient will need to provide this information.</strong>
					</AlertMessage>
				);
				secondaryInsuranceDetails = null;
			 }
		 } else {
			 insuranceDetails = <InsuranceDetails insuranceInfo={insuranceInfo} patientInfo={patientInfo} />;
			 secondaryInsuranceDetails = <InsuranceDetails insuranceInfo={secondaryInsuranceInfo} patientInfo={patientInfo} />;
		 }
		if (insuranceDetails) {
			insuranceSection = (<InsuranceInformation
					  primaryInsuranceDetails={insuranceDetails}
					  secondaryInsuranceDetails={secondaryInsuranceDetails}
					  gearOrder={this.props.gearOrder}
					  editingSection={editingSection}
					  editingReq={editingReq}
					  lastSavedReq={lastSavedReq}
					  lastSavedSection={lastSavedSection}
					  userInfo={this.props.userInfo}
					  cancelEdit={this.cancelEdit}
					  saveEdit={this.saveEdit}
					  canEditInsurance={canEditAny}
					  isEditSaveInProgress={this.state.isEditSaveInProgress}
					  editRef={this.editRef}
					  inlineSaveError={this.state.inlineSaveError}
					  editSection={canEditInsurance && this.editSection}
					  insuranceOptions={this.props.insuranceOptions}
					  setInsuranceOptions={this.props.setInsuranceOptions}
				 />
			);
		}

		// Show a different message after information has been confirmed, and hide it if no reqs are on the order
		// or if we are showing the practitioner summary
		let confirmAlert;
		if ( this.props.gearOrder.requisitions.length === 0 || practitionerSummary ) {
			confirmAlert = null;
		} else if ( fullyAccessioned && !practitionerSummary ) {
			confirmAlert = 
				<AccessionedOrderAlert />;
		}

		let buttonDivClass = "mt-20";
		if( this.state.confirmed && !this.state.editingSection ) {
			buttonDivClass = "sticky-bottom";
		}

		let requisitions = this.props.gearOrder.requisitions.map((requisition, index) => {

			let omsRequisition = this.state.omsRequisitions?.find(omsRequisition => {
				if( omsRequisition.id === requisition.reqNumber ) {
					return true;
				} else {
					let omsReqOrderableItemId = (omsRequisition.orderableItem || {}).id;
					return requisition.omsOrderableItemId && Number(omsReqOrderableItemId) === Number(requisition.omsOrderableItemId);
				}
			}) || null;
			
			let price = this.props.prices.find(price => price.gearRequisitionId === requisition.id);

			return <RequisitionDetails
				key={requisition?.id || index}
				requisition={requisition}
				canEditAny={canEditAny}
				gearOrder={this.props.gearOrder}
				omsRequisition={omsRequisition}
				editingSection={editingSection}
				editingReq={editingReq}
				lastSavedReq={lastSavedReq}
				lastSavedSection={lastSavedSection}
				index={index}
				userInfo={this.props.userInfo}
				price={price}
				cancelEdit={this.cancelEdit}
				saveEdit={this.saveEdit}
				isEditSaveInProgress={this.state.isEditSaveInProgress}
				editRef={this.editRef}
				inlineSaveError={this.state.inlineSaveError}
				getWorkflowStep={this.props.getWorkflowStep}
				editSection={this.editSection}
				confirmed={this.state.confirmed}
				recommendedProductOrderableItemIds={this.state.recommendedProductOrderableItemIds}
				showRequisition={this.showRequisition}
				removeClickHandler={this.removeClickHandler}
				hasMixedPricingReqs={this.state.hasMixedPricingReqs}
				medicalNecessityRequired={this.state.medicalNecessityRequired}
				domesticRequisitionType={this.state.domesticRequisitionType}
				testSelectionsMissing={this.state.gearReqsNeedingTestSelections?.includes(requisition.id)}
				doNotQuotePrices={this.props.doNotQuotePrices}
			/>
		});

		// Do not show payment step when all requisitions have been accessioned
		const paymentSection = fullyAccessioned ? null : this.buildPaymentDisplay();
		
		let nextButtonText;
		if (this.state.confirmed || practitionerSummary) {
			nextButtonText = 'Next';
		} else {
			nextButtonText = 'Submit';
		}

		let pageTitle = <h2>Information Review{!this.props.gearOrder.containsRegistrationRequisition && <span> - <small>Please confirm the data is accurate and submit your order.</small></span>}</h2>;
		let summaryText = null;
		if (practitionerSummary) {
			pageTitle = <h2>Practitioner Summary</h2>;
			summaryText = <p>The following details were entered by your practitioner. Please continue to the next steps to fill out any missing information.</p>;
		}

		// Is this order for a Government payor (Medicare/Tricare)
		const isGovernmentOrder = isGovernmentPayorOrder(this.props.gearOrder);
		const showMedicalNecessity = !this.state.hasMixedPricingReqs && this.state.medicalNecessityRequired;

		// SCR-6843: Show a warning if a Nutritional product was selected and the patient's age is less than 2 years old
		const showNutritionalAgeDisclaimer =
			!fullyAccessioned && 
			this.props.gearOrder?.patientInfo?.dateOfBirth &&
			this.props.gearOrder?.requisitions?.some(
				(req) => req?.omsOrderableItemCategory?.toLowerCase() === 'nutritional'
			) &&
			calculateAge(getDateFromIso8601String(this.props.gearOrder.patientInfo.dateOfBirth)) < 2;

		return (
			<div>
					<div>
						<div>
							{pageTitle}
							{summaryText}
						</div>
						{confirmAlert}
						{showNutritionalAgeDisclaimer && (
							<div className="alert alert-danger h3">Nutritional testing is not available for patients less than 2 years of age. The specimens will be discarded.</div>
						)}
						<ReviewStatus
							gearOrder={this.props.gearOrder} 
							userInfo={this.props.userInfo} 
							practAgreementRequired={this.state.practAgreement.required}
							medicalNecessityRequired={this.state.medicalNecessityRequired}
							getValidatedPatientInfoForm={this.getValidatedPatientInfoForm} />
						{(customerType === 'prc') && (
							<MedicareDiscardAlert gearOrder={this.props.gearOrder} />
						)}
						{(isGovernmentOrder && this.props.userInfo.type === 'myGDX' && !showMedicalNecessity) && 
							<MedicareOrderAlert />
						}
						{showMedicalNecessity && (
							<div data-step="medicare">
								<MedicalNecessityDisclaimer>
									<MedicareObligationText />
									<div className={"text-right mt-5" + (this.state.medicalNecessityAcknowledged.valid === false ? " has-error" : "")}>
										<label className={"control-label mb-0"} style={{cursor: 'pointer'}} htmlFor="mnAcknowledgement">
											<input id="mnAcknowledgement" type="checkbox" value="true" name="mnAcknowledgement" 
												checked={!!this.state.medicalNecessityAcknowledged.value}
												required={true}
												onChange={this.setMedicalNecessityAcknowledged}
												aria-describedby='mnAcknowledgement-help' />
												{' '}I acknowledge I have read the Definition of Medical Necessity.<strong className="text-danger"> *</strong>
										</label>
										{this.state.medicalNecessityAcknowledged.valid === false && <div id="mnAcknowledgement-help" className="help-block mb-0 mt-0 validation-message">{this.state.medicalNecessityAcknowledged.message}</div>}
									</div>
								</MedicalNecessityDisclaimer>
							</div>
						)}
						<OrderNotes gearOrder={this.props.gearOrder} userInfo={this.props.userInfo} omsRequisitions={this.state.omsRequisitions} />
						<AlertMessage display={this.state.showError === true} type="danger">
							<strong>{this.state.errorMessage}</strong>
						</AlertMessage>
					</div>
					{!this.props.gearOrder.physicianInTheLoop && (
						<PractitionerDetails
							ref={(editingSection === 'practitioner') ? this.editRef : null}
							gearOrder={this.props.gearOrder}
							userInfo={this.props.userInfo}
							isEditing={editingSection === 'practitioner'}
							onEdit={!fullyAccessioned && (() => { this.editSection('practitioner') })}
							onCancel={this.cancelEdit}
							onSave={this.saveEdit}
							isEditSaveInProgress={this.state.isEditSaveInProgress}
						/>
					)}
					{requisitions}
					<RecommendedProducts
						gearOrder={this.props.gearOrder}
						userInfo={this.props.userInfo}
						addNewRequisition={this.addNewRequisition}
						loadSavedRecommendedProduct={this.loadSavedRecommendedProduct}
					/>
					{patientSection}
					{insuranceSection}
					{paymentSection}
					<AlertMessage display={!!this.state.submitErrorMessage} type="danger">
						<strong className='validation-message'>{this.state.submitErrorMessage}</strong>
					</AlertMessage>
					{(this.state.practAgreement.required && canAuthorizeOrder(this.props.userInfo, this.props.gearOrder)) && (
						<div className={"space-bottom form-group text-right"} data-step="signature">
							<div className={"checkbox" + (this.state.practAgreement.valid === false ? " has-error" : "")}>
							<div>{this.props.gearOrder.authorizingPractitioner.firstName} {this.props.gearOrder.authorizingPractitioner.lastName}, {this.props.gearOrder.authorizingPractitioner.degree} ({this.props.gearOrder.authorizingPractitioner.gsdlNo})</div>
								<label className="control-label" htmlFor="practCheckBox">
									{this.state.practAgreement.saveInProgress ? (
										<i className="fa fa-spinner fa-spin fa-fw mr-10" aria-hidden="true"></i>
									) : (
										<input
											id="practCheckBox"
											type="checkbox"
											checked={this.state.practAgreement.value}
											disabled={
												this.state.practAgreement.value || this.state.practAgreement.saveInProgress
											}
											onChange={this.setPractAgreement}
										/>
									)}									
									<strong>I agree that I am the authorizing practitioner, or that I am authorized to order on their behalf.<span className="text-danger">*</span></strong>
								</label>
								{this.state.practAgreement.valid === false &&
									<div className="validation-message text-danger">{this.state.practAgreement.message}</div>
								}
								{this.state.practAgreement.valid === true &&
									<div className="validation-message text-success">Your authorization has been saved. Please submit the order to confirm.</div>
								}
							</div>
						</div>
					)}
					<div className={buttonDivClass}>
						<button disabled={isSaveDisabled} className="btn btn-primary radius btn-sm margin-clear pull-right" type="submit" onClick={this.handleSubmit} >
							{nextButtonText}
							<i className="pl-10 fa fa-arrow-circle-right"></i>
						</button>
						<button className="btn btn-white radius btn-sm margin-clear" type="button" onClick={this.goBack}>
							<i className="pl-10 fa fa-arrow-circle-left"></i> &nbsp;
							Back
						</button>
					</div>
				{this.state.displayRemoveRequisition && (
					<RemoveRequisition
						gearOrderId={this.props.gearOrder.id}
						requisition={this.state.requisitionToRemove}
						removeRequisitionResponseHandler={this.removeRequisitionResponseHandler}
						modalHidden={() => this.setState({ displayRemoveRequisition: false, saveForLater: false })}
						saveForLater={this.state.saveForLater}
						queryString={this.props.location?.search}
						requisitionCount={this.props.gearOrder.requisitions?.length ?? 0}
					/>
				)}
				{this.state.isShowUnsavedChangesConfirmation && <UnsavedChangesModal onConfirm={this.clearUnsavedChanges} onCancel={this.handleUnsavedChanges} />}
			</div>
		);
	}
}

/**
 * Build a list of GearReq Ids that need to have test selections saved or confirmed.
 */
const findGearRequisitionsNeedingTestSelections = (gearOrder, omsRequisitions) => {
	if (!gearOrder?.requisitions?.length) {
		return [];
	}

	const gearReqIdsNeedingTestSelection = [];
	const practitioner = gearOrder.practitioner;
	gearOrder.requisitions.forEach((gearRequisition) => {
		// Require a panel or components to be selected
		const testsNotSelected =
			gearRequisition.panel === '0' &&
			!gearRequisition.componentOptions?.length;
		if (testsNotSelected) {
			gearReqIdsNeedingTestSelection.push(gearRequisition.id);
			return;
		}

		// The rest of the checks are only considered if the panel step was last saved by the "system" user
		const panelStepSavedBySystem = gearOrder?.workflow?.steps?.find(
			(step) =>
				step.id === 2 && step.gearReqId === gearRequisition.id && step.userType === 'system'
		);
		if (!panelStepSavedBySystem) {
			return;
		}

		// Find the omsRequisition that goes with this gearRequisition
		const omsRequisition = omsRequisitions?.find(
			(omsReq) => omsReq?.orderableItem?.id === gearRequisition.omsOrderableItemId
		);

		// Profile-specific fields are missing if the OMS req contains specific fields and the gearReq does not have profile-specific values
		if (!gearRequisition.profileSpecificFields?.length && omsRequisition?.orderableItem?.inputFields?.length) {
			const profileSpecificFields = [
				'Hormone Replacement Therapy',
				'Premenopausal',
				'Pre-provocation'
			];
			const missingProfileSpecificFields = omsRequisition.orderableItem.inputFields.some((inputField) =>
				profileSpecificFields.includes(inputField.accessionKey)
			);
			if (missingProfileSpecificFields) {
				gearReqIdsNeedingTestSelection.push(gearRequisition.id);
				return;
			}
		}

		// Require test selections if billing method can be anything other than Client or Patient
		if (
			gearRequisition.billingMethod !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE &&
			gearRequisition.billingMethod !== process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE &&
			getSupportedBillingMethodNames(practitioner, omsRequisition?.orderableItem).some(
				(billingMethod) =>
					billingMethod !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE &&
					billingMethod !== process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE
			)
		) {
			gearReqIdsNeedingTestSelection.push(gearRequisition.id);
			return;
		}
	});

	return gearReqIdsNeedingTestSelection;
}

// wrap the component in the workflow HOC to get access to common workflow logic
export default withWorkflow(OrderConfirmation);
